"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  Panel,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type CoordinateAxis,
  type CoordinateError,
  parseCoordinate,
} from "@/lib/tool-kit/earth-science/coordinates";
import {
  compassIndex,
  type DistanceUnit,
  EARTH_MEAN_RADIUS_M,
  finalBearing,
  haversineDistance,
  haversineTerm,
  initialBearing,
  type LatLon,
  metresTo,
  midpoint,
  vincentyInverse,
} from "@/lib/tool-kit/earth-science/geodesy";

/** English strings. A translated content file overrides these through `ui`. */
export const LATITUDE_LONGITUDE_DISTANCE_CALCULATOR_STRINGS = {
  inputHint:
    "Decimal degrees (40.6413, −73.7781) or degrees, minutes and seconds (40°38′29″ N). A trailing N, S, E or W also works.",
  pointA: "Point A",
  pointB: "Point B",
  latitude: "Latitude",
  longitude: "Longitude",
  unitLabel: "Distance unit",
  unitKm: "kilometres (km)",
  unitMi: "miles (mi)",
  unitNmi: "nautical miles (nmi)",
  unitM: "metres (m)",
  swap: "Swap points A and B",
  results: "Results",
  haversine: "Great-circle distance (haversine, sphere)",
  vincenty: "Ellipsoidal distance (Vincenty, WGS84)",
  difference: "Difference between the two",
  bearing: "Initial bearing A → B",
  finalBearing: "Final bearing on arrival at B",
  midpoint: "Midpoint of the great circle",
  degreesSymbol: "°",
  compassN: "N",
  compassNNE: "NNE",
  compassNE: "NE",
  compassENE: "ENE",
  compassE: "E",
  compassESE: "ESE",
  compassSE: "SE",
  compassSSE: "SSE",
  compassS: "S",
  compassSSW: "SSW",
  compassSW: "SW",
  compassWSW: "WSW",
  compassW: "W",
  compassWNW: "WNW",
  compassNW: "NW",
  compassNNW: "NNW",
  tableHeading: "The distance in every unit",
  columnUnit: "Unit",
  columnHaversine: "Haversine",
  columnVincenty: "Vincenty",
  workingHeading: "The working (haversine)",
  stepA: "a = sin²(Δφ / 2) + cos φ₁ × cos φ₂ × sin²(Δλ / 2) = {a}",
  stepC: "c = 2 × atan2(√a, √(1 − a)) = {c} rad",
  stepD: "d = R × c = {radius} km × {c} = {d} km",
  noVincenty:
    "Vincenty's iteration does not converge for points that are almost exactly opposite each other on the globe. The haversine result above is still valid there, within its usual error.",
  accuracyNote:
    "The haversine distance treats the Earth as a sphere of radius 6371.0088 km (the IUGG mean radius) and can be off by up to about 0.5 %. Vincenty's formula measures along the WGS84 ellipsoid and is accurate to about half a millimetre. Distances are along the surface, at sea level; they do not include height or road routes.",
  copyResults: "Copy results",
  copied: "Copied!",
  copyLine:
    "{a} to {b}: {haversine} (haversine), {vincenty} (Vincenty), bearing {bearing}",
  enterValues:
    "Enter a latitude and longitude for both points to see the distance.",
  problem: "{field} {message}",
  fieldPointALat: "Point A latitude",
  fieldPointALon: "Point A longitude",
  fieldPointBLat: "Point B latitude",
  fieldPointBLon: "Point B longitude",
  errors: {
    empty: "needs a value.",
    format:
      "could not be read. Use decimal degrees or degrees, minutes and seconds.",
    range: "is out of range (latitude ±90°, longitude ±180°).",
    minutes:
      "has minutes that must be below 60 (and whole when seconds follow).",
    seconds: "has seconds that must be below 60.",
    hemisphere:
      "has both a minus sign and a hemisphere letter. Use one or the other.",
    axis: "uses the wrong hemisphere letter (N or S for latitude, E or W for longitude).",
  },
} as const;

type Strings = typeof LATITUDE_LONGITUDE_DISTANCE_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const COMPASS_KEYS = [
  "compassN",
  "compassNNE",
  "compassNE",
  "compassENE",
  "compassE",
  "compassESE",
  "compassSE",
  "compassSSE",
  "compassS",
  "compassSSW",
  "compassSW",
  "compassWSW",
  "compassW",
  "compassWNW",
  "compassNW",
  "compassNNW",
] as const;

const UNIT_KEYS = {
  km: "unitKm",
  mi: "unitMi",
  nmi: "unitNmi",
  m: "unitM",
} as const satisfies Record<DistanceUnit, keyof Strings>;
const UNIT_SYMBOLS: Record<DistanceUnit, string> = {
  km: "km",
  mi: "mi",
  nmi: "nmi",
  m: "m",
};
const UNITS = Object.keys(UNIT_KEYS) as DistanceUnit[];

const TWO = { maximumFractionDigits: 2 };
const FOUR = { maximumFractionDigits: 4 };
const SIX = { maximumFractionDigits: 6 };

function distanceText(fmt: Fmt, metres: number, unit: DistanceUnit): string {
  const value = metresTo(metres, unit);
  const options = unit === "m" ? TWO : { maximumFractionDigits: 3 };
  return `${fmt(value, options)} ${UNIT_SYMBOLS[unit]}`;
}

interface Entries {
  aLat: string;
  aLon: string;
  bLat: string;
  bLon: string;
}

type FieldKey =
  | "fieldPointALat"
  | "fieldPointALon"
  | "fieldPointBLat"
  | "fieldPointBLon";

const FIELD_ORDER = [
  ["aLat", "lat", "fieldPointALat"],
  ["aLon", "lon", "fieldPointALon"],
  ["bLat", "lat", "fieldPointBLat"],
  ["bLon", "lon", "fieldPointBLon"],
] as const satisfies readonly (readonly [
  keyof Entries,
  CoordinateAxis,
  keyof Strings,
])[];

type Reading =
  | { kind: "empty" }
  | { kind: "error"; field: FieldKey; error: CoordinateError }
  | { kind: "ok"; a: LatLon; b: LatLon };

function readEntries(entries: Entries): Reading {
  const values: Record<string, number> = {};
  let blank = 0;
  for (const [key, axis, field] of FIELD_ORDER) {
    const parsed = parseCoordinate(entries[key], axis);
    if (!parsed.ok) {
      if (parsed.error === "empty") {
        blank += 1;
        continue;
      }
      return { kind: "error", field, error: parsed.error };
    }
    values[key] = parsed.value;
  }
  if (blank > 0) {
    return { kind: "empty" };
  }
  return {
    kind: "ok",
    a: { lat: values.aLat, lon: values.aLon },
    b: { lat: values.bLat, lon: values.bLon },
  };
}

function bearingText(strings: Strings, fmt: Fmt, bearing: number): string {
  return `${fmt(bearing, { maximumFractionDigits: 1 })}${strings.degreesSymbol} ${strings[COMPASS_KEYS[compassIndex(bearing)]]}`;
}

function Results({
  a,
  b,
  unit,
  strings,
  fmt,
}: {
  a: LatLon;
  b: LatLon;
  unit: DistanceUnit;
  strings: Strings;
  fmt: Fmt;
}) {
  const sphere = haversineDistance(a, b);
  const ellipsoid = vincentyInverse(a, b);
  const bearing = initialBearing(a, b);
  const arrival = finalBearing(a, b);
  const mid = midpoint(a, b);
  const term = haversineTerm(a, b);
  const angle = sphere / EARTH_MEAN_RADIUS_M;
  const gap = ellipsoid === null ? null : sphere - ellipsoid.distance;
  const percent =
    ellipsoid === null || ellipsoid.distance === 0
      ? null
      : ((sphere - ellipsoid.distance) / ellipsoid.distance) * 100;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.haversine}
          value={distanceText(fmt, sphere, unit)}
        />
        <StatCard
          emphasis
          label={strings.vincenty}
          value={
            ellipsoid === null
              ? "—"
              : distanceText(fmt, ellipsoid.distance, unit)
          }
        />
        <StatCard
          label={strings.difference}
          value={
            gap === null || percent === null
              ? "—"
              : `${distanceText(fmt, Math.abs(gap), unit)} (${fmt(Math.abs(percent), TWO)} %)`
          }
        />
        <StatCard
          label={strings.bearing}
          value={bearingText(strings, fmt, bearing)}
        />
        <StatCard
          label={strings.finalBearing}
          value={bearingText(strings, fmt, arrival)}
        />
        <StatCard
          label={strings.midpoint}
          value={`${fmt(mid.lat, FOUR)}, ${fmt(mid.lon, FOUR)}`}
        />
      </StatGrid>
      {ellipsoid === null ? <Notice>{strings.noVincenty}</Notice> : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={t(strings.copyLine, {
          a: `${fmt(a.lat, SIX)}, ${fmt(a.lon, SIX)}`,
          b: `${fmt(b.lat, SIX)}, ${fmt(b.lon, SIX)}`,
          haversine: distanceText(fmt, sphere, unit),
          vincenty:
            ellipsoid === null
              ? "—"
              : distanceText(fmt, ellipsoid.distance, unit),
          bearing: bearingText(strings, fmt, bearing),
        })}
      />
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.columnUnit,
          strings.columnHaversine,
          strings.columnVincenty,
        ]}
        rows={UNITS.map((each) => [
          strings[UNIT_KEYS[each]],
          distanceText(fmt, sphere, each),
          ellipsoid === null
            ? "—"
            : distanceText(fmt, ellipsoid.distance, each),
        ])}
      />
      <Panel title={strings.workingHeading}>
        <ol className="list-decimal space-y-1 pl-5 text-foreground text-sm">
          <li>
            {t(strings.stepA, { a: fmt(term, { maximumFractionDigits: 8 }) })}
          </li>
          <li>
            {t(strings.stepC, { c: fmt(angle, { maximumFractionDigits: 8 }) })}
          </li>
          <li>
            {t(strings.stepD, {
              radius: fmt(EARTH_MEAN_RADIUS_M / 1000, {
                maximumFractionDigits: 4,
              }),
              c: fmt(angle, { maximumFractionDigits: 8 }),
              d: fmt(sphere / 1000, { maximumFractionDigits: 3 }),
            })}
          </li>
        </ol>
      </Panel>
      <p className={HINT}>{strings.accuracyNote}</p>
    </>
  );
}

function PointFields({
  title,
  prefix,
  lat,
  lon,
  onLat,
  onLon,
  strings,
}: {
  title: string;
  prefix: string;
  lat: string;
  lon: string;
  onLat: (value: string) => void;
  onLon: (value: string) => void;
  strings: Strings;
}) {
  return (
    <Panel title={title}>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id={`${prefix}-lat`}
          label={strings.latitude}
          numeric
          onChange={onLat}
          value={lat}
        />
        <TextField
          id={`${prefix}-lon`}
          label={strings.longitude}
          numeric
          onChange={onLon}
          value={lon}
        />
      </div>
    </Panel>
  );
}

export default function LatitudeLongitudeDistanceCalculatorWidget() {
  const strings = useToolUi(LATITUDE_LONGITUDE_DISTANCE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [entries, setEntries] = useState<Entries>({
    aLat: "40.6413",
    aLon: "-73.7781",
    bLat: "51.4700",
    bLon: "-0.4543",
  });
  const [unit, setUnit] = useState<DistanceUnit>("km");
  const reading = readEntries(entries);

  function renderOutcome() {
    if (reading.kind === "empty") {
      return <Notice>{strings.enterValues}</Notice>;
    }
    if (reading.kind === "error") {
      return (
        <ErrorNotice>
          {t(strings.problem, {
            field: strings[reading.field],
            message: strings.errors[reading.error],
          })}
        </ErrorNotice>
      );
    }
    return (
      <Results
        a={reading.a}
        b={reading.b}
        fmt={fmt}
        strings={strings}
        unit={unit}
      />
    );
  }

  return (
    <div className="space-y-6">
      <p className={HINT}>{strings.inputHint}</p>
      <div className="grid gap-4 md:grid-cols-2">
        <PointFields
          lat={entries.aLat}
          lon={entries.aLon}
          onLat={(aLat) => setEntries({ ...entries, aLat })}
          onLon={(aLon) => setEntries({ ...entries, aLon })}
          prefix="lldc-a"
          strings={strings}
          title={strings.pointA}
        />
        <PointFields
          lat={entries.bLat}
          lon={entries.bLon}
          onLat={(bLat) => setEntries({ ...entries, bLat })}
          onLon={(bLon) => setEntries({ ...entries, bLon })}
          prefix="lldc-b"
          strings={strings}
          title={strings.pointB}
        />
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-60">
          <SelectField
            id="lldc-unit"
            label={strings.unitLabel}
            onChange={setUnit}
            options={UNITS.map((each) => ({
              value: each,
              label: strings[UNIT_KEYS[each]],
            }))}
            value={unit}
          />
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setEntries({
              aLat: entries.bLat,
              aLon: entries.bLon,
              bLat: entries.aLat,
              bLon: entries.aLon,
            })
          }
          type="button"
        >
          {strings.swap}
        </button>
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {renderOutcome()}
      </section>
    </div>
  );
}
